if (!customElements.get('product-gifting')) {
  customElements.define('product-gifting', class extends HTMLElement {
    connectedCallback() {
      this.hidden = false;
      this.dirty = false;
      this.onChange = () => { this.dirty = true; };
      this.addEventListener('change', this.onChange);
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.sync());
      this.sync();
    }

    disconnectedCallback() {
      this.unsubscribe?.();
      this.removeEventListener('change', this.onChange);
    }

    get variantIds() {
      return this.dataset.variants.split(',').map(Number).filter(Boolean);
    }

    get configurableGiftHandles() {
      return this.dataset.configurableGiftHandles.split(',').filter(Boolean);
    }

    async getCart() {
      const response = await fetch(`${routes.cart_url}.js`);
      if (!response.ok) throw new Error('Unable to check gift packaging. Please try again.');
      return response.json();
    }

    async sync() {
      try {
        const cart = await this.getCart();
        if (!this.isConnected || this.dirty) return;
        const existing = cart.items.find((item) => this.variantIds.includes(item.variant_id));
        const input = this.querySelector(`input[value="${existing?.variant_id || 0}"]`);
        if (input) input.checked = true;
        this.querySelector('[role="status"]').textContent = existing
          ? 'Gift packaging is already in your cart. This choice applies to your whole order.' : '';
      } catch (_) {
        // Submission checks the cart again and reports failures before adding anything.
      }
    }

    async addToCart(formData, config) {
      const selected = Number(this.querySelector('input:checked')?.value || 0);
      const explicitChoice = this.dirty;
      const fieldset = this.querySelector('fieldset');
      fieldset.disabled = true;
      try {
        const cart = await this.getCart();
        const existing = cart.items.filter((item) => this.variantIds.includes(item.variant_id));
        // A stale, untouched product page must not remove a newer cart choice.
        let requested = explicitChoice ? selected : (existing[0]?.variant_id || 0);
        const existingConfigurableGift = cart.items.find((item) =>
          this.configurableGiftHandles.includes(item.handle)
        );
        const addsPackaging = requested && !existing.some((item) => item.variant_id === requested);
        if (addsPackaging && existingConfigurableGift) {
          const confirmed =
            typeof window.confirmExtraGiftPackaging === 'function' &&
            (await window.confirmExtraGiftPackaging(
              existingConfigurableGift.product_title || existingConfigurableGift.title
            ));
          if (!confirmed) requested = existing[0]?.variant_id || 0;
        }
        const body = new FormData();
        for (const [key, value] of formData.entries()) {
          if (key.startsWith('gift-choice-') || key === 'add') continue;
          if (key === 'sections' || key === 'sections_url') body.append(key, value);
          else {
            const bracket = key.indexOf('[');
            const nestedKey = bracket < 0 ? `[${key}]` : `[${key.slice(0, bracket)}]${key.slice(bracket)}`;
            body.append(`items[0]${nestedKey}`, value);
          }
        }
        if (requested && !existing.some((item) => item.variant_id === requested)) {
          body.append('items[1][id]', requested);
          body.append('items[1][quantity]', '1');
        }
        const response = await fetch(routes.cart_add_url, { ...config, body });
        const result = await response.json();
        if (!response.ok || result.status) return result;
        const primary = result.items?.find((item) => String(item.variant_id) === String(formData.get('id'))) || result.items?.[0] || result;
        const updates = {};
        let kept = false;
        existing.forEach((item) => {
          const quantity = item.variant_id === requested && !kept ? 1 : 0;
          if (quantity) kept = true;
          if (item.quantity !== quantity) updates[item.key] = quantity;
        });
        if (Object.keys(updates).length) {
          try {
            const updateResponse = await fetch(routes.cart_update_url, {
              ...fetchConfig(),
              body: JSON.stringify({ updates, sections: formData.get('sections')?.split(','), sections_url: window.location.pathname }),
            });
            const updated = await updateResponse.json();
            if (!updateResponse.ok || updated.errors) throw new Error('Packaging update failed');
            result.sections = updated.sections;
          } catch (_) {
            // The product was added: send the customer to the cart instead of inviting a duplicate retry.
            window.location.assign(routes.cart_url);
            return { ...primary, sections: result.sections };
          }
        }
        this.dirty = false;
        return { ...primary, sections: result.sections };
      } finally {
        fieldset.disabled = false;
      }
    }
  });
}

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        const productChoices = this.form.querySelector('[data-product-choices]');
        if (productChoices) {
          const missingChoice = Array.from(productChoices.querySelectorAll('select')).find(
            (select) => !select.value || select.selectedOptions[0]?.disabled
          );
          if (missingChoice) {
            this.handleErrorMessage(productChoices.dataset.validationMessage);
            missingChoice.focus();
            return;
          }
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        const gifting = this.form.querySelector('product-gifting');
        (gifting ? gifting.addToCart(formData, config) : fetch(`${routes.cart_add_url}`, config).then((response) => response.json()))
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.handleErrorMessage(e.message || window.cartStrings.error);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
